import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Globe, CheckCircle, XCircle, Eye, Edit, Trash2 } from 'lucide-react'
import merchantService, { Organization } from '@/services/merchantService'
import { DetailHeader } from '@/components/ui/DetailHeader'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

const OrganizationList = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilters, setActiveFilters] = useState<any[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  })
  const navigate = useNavigate()

  useEffect(() => {
    loadOrganizations()
  }, [])

  const loadOrganizations = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await merchantService.getOrganizations()
      setOrganizations(response.organizations)
      setPagination(prev => ({
        ...prev,
        total: response.organizations.length
      }))
    } catch (err: any) {
      console.error('Error loading organizations:', err)
      setError(err.response?.data?.error || 'Erreur lors du chargement des organisations')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette organisation ?')) {
      return
    }
    
    try {
      await merchantService.deleteOrganization(id)
      alert('Organisation supprimée avec succès')
      loadOrganizations() // Reload after deletion
    } catch (err: any) {
      console.error('Error deleting organization:', err)
      alert(err.response?.data?.error || 'Erreur lors de la suppression')
    }
  }

  const handleToggleStatus = async (organization: Organization) => {
    try {
      await merchantService.updateOrganization(organization.organization_id, {
        is_active: !organization.is_active
      })
      alert(`Organisation ${!organization.is_active ? 'activée' : 'désactivée'} avec succès`)
      loadOrganizations() // Reload after update
    } catch (err: any) {
      console.error('Error toggling organization status:', err)
      alert(err.response?.data?.error || 'Erreur lors de la modification du statut')
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Organisation',
      sortable: true,
      render: (_value: any, row: Organization) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Globe className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {row.name}
            </div>
            <div className="text-sm text-gray-500">
              ID : {row.organization_id ? row.organization_id.substring(0, 8) + '...' : 'N/D'}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      render: (_value: any, row: Organization) => (
        <div className="text-sm text-gray-900 max-w-xs truncate">
          {row.description || 'Aucune description'}
        </div>
      )
    },
    {
      key: 'created_at',
      label: 'Date de création',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-900">
          {formatDate(value)}
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Statut',
      sortable: true,
      render: (_value: any, row: Organization) => (
        row.is_active ? (
          <Badge variant="default" className="inline-flex items-center bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Actif
          </Badge>
        ) : (
          <Badge variant="destructive" className="inline-flex items-center">
            <XCircle className="h-3 w-3 mr-1" />
            Inactif
          </Badge>
        )
      )
    }
  ], [])

  const actions = useMemo(() => [
    {
      label: 'Voir',
      icon: <Eye className="h-4 w-4" />,
      onClick: (row: Organization) => navigate(`/organizations/${row.organization_id}`)
    },
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: (row: Organization) => navigate(`/organizations/${row.organization_id}/edit`)
    },
    {
      label: 'Activer/Désactiver',
      icon: <CheckCircle className="h-4 w-4" />,
      onClick: (row: Organization) => handleToggleStatus(row)
    },
    {
      label: 'Supprimer',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (row: Organization) => handleDelete(row.organization_id)
    }
  ], [navigate])

  const filterOptions = useMemo(() => [
    {
      key: 'is_active',
      label: 'Statut',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'true', label: 'Actif' },
        { value: 'false', label: 'Inactif' }
      ]
    }
  ], [])

  const handleFilterChange = (newFilters: any[]) => {
    setActiveFilters(newFilters)
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value)
  }

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }))
  }

  const handlePageSizeChange = (newSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newSize, page: 1 }))
  }

  const filteredData = useMemo(() => {
    let result = organizations

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(org => 
        org.name.toLowerCase().includes(query) || 
        (org.description?.toLowerCase() || '').includes(query)
      )
    }

    // Apply other filters
    activeFilters.forEach(filter => {
      if (filter.key === 'is_active' && filter.value !== 'all') {
        const isActive = filter.value === 'true'
        result = result.filter(org => org.is_active === isActive)
      }
    })

    // Update pagination total
    setPagination(prev => ({ ...prev, total: result.length }))

    return result
  }, [organizations, searchQuery, activeFilters])

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize
    const endIndex = startIndex + pagination.pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, pagination.page, pagination.pageSize])

  const breadcrumbs = [
    { label: 'Tableau de bord', href: '/' },
    { label: 'Organisations', href: '/organizations' }
  ]

  const headerActions = [
    {
      label: 'Nouvelle Organisation',
      icon: <Plus className="h-4 w-4 mr-2" />,
      onClick: () => navigate('/organizations/new'),
      variant: 'default' as const
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Organisations (Marques Globales)"
        subtitle="Gérez les organisations globales qui regroupent les marchands"
        breadcrumbs={breadcrumbs}
        actions={headerActions}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
          <div className="mt-4">
            <Button variant="outline" onClick={loadOrganizations}>
              Réessayer
            </Button>
          </div>
        </Alert>
      )}

      <FilterBar
        searchPlaceholder="Rechercher par nom ou description..."
        filters={filterOptions}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
      />

      <DataTable
        columns={columns}
        data={paginatedData}
        actions={actions}
        loading={loading}
        emptyMessage="Aucune organisation trouvée"
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
      />
    </div>
  )
}

export default OrganizationList